import { useState, type FormEvent } from "react";

const FORM_ID = "daikmtroh653ivfvofag";
const SUBMIT_ADDR = "https://readdy.ai/api/form/daikmtroh653ivfvofag";

type Status = "idle" | "submitting" | "success" | "error";

export default function SubscribeSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = String(formData.get("website_alt") || "").trim();

    if (honeypot) {
      setStatus("success");
      formEl.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const raw = String(value).trim();
      if (!raw) return;
      payload.append(key, raw);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam =
        typeof serverMsg === "string" &&
        (serverMsg.toLowerCase().includes("spam") || serverMsg.includes("垃圾"));

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setFormError(serverMsg || "提交未成功，请稍后重试。");
        setStatus("error");
        return;
      }

      setStatus("success");
      formEl.reset();
    } catch {
      setFormError("网络连接异常，请检查网络后重试。");
      setStatus("error");
    }
  };

  return (
    <section id="subscribe" className="scroll-mt-24 bg-foreground-950 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-500/15 px-3 py-1 text-xs font-medium tracking-wider text-accent-400">
              <i className="ri-send-plane-line" />
              深度周报
            </span>
            <h2 className="mt-5 font-heading text-2xl font-bold leading-snug text-background-50 md:text-4xl">
              每周一封，把本周最值得留下的
              <span className="text-accent-400">档案</span>寄给你
            </h2>
            <p className="mt-5 max-w-lg text-sm leading-7 text-background-300 md:text-base">
              订阅后可收到本周更新的深度专题、被读者反复翻看的旧档案，以及一份「信息流向简报」。不做标题党，不堆热点，随时可取消。
            </p>
            <ul className="mt-7 flex flex-col gap-3 text-sm text-background-200">
              <li className="flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-accent-400" />
                每周更新一次，绝不频繁打扰
              </li>
              <li className="flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-accent-400" />
                只包含可核实节点的信息
              </li>
              <li className="flex items-center gap-3">
                <i className="ri-checkbox-circle-line text-accent-400" />
                随时一键退订，不保留邮箱
              </li>
            </ul>
          </div>

          <div className="rounded-xl border border-background-800 bg-background-900/40 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-start gap-4 py-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
                  <i className="ri-check-line text-2xl" />
                </span>
                <h3 className="font-heading text-xl font-semibold text-background-50">
                  订阅成功
                </h3>
                <p className="text-sm leading-7 text-background-300">
                  我们已经收到你的邮箱。下一期深度周报将发送到你的收件箱，请留意查收。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="whitespace-nowrap rounded-md border border-background-700 px-4 py-2 text-sm text-background-200 transition-colors hover:border-accent-400 hover:text-accent-400"
                >
                  再订阅一个邮箱
                </button>
              </div>
            ) : (
              <form
                id={`form-${FORM_ID}`}
                data-readdy-form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5"
                noValidate
              >
                <div className="flex flex-col gap-2">
                  <label htmlFor="sub-email" className="text-sm font-medium text-background-200">
                    邮箱地址 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="sub-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-background-50 outline-none transition-colors placeholder:text-background-500 focus:border-accent-400"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="sub-interest" className="text-sm font-medium text-background-200">
                    最感兴趣的栏目
                  </label>
                  <select
                    id="sub-interest"
                    name="interest"
                    defaultValue="商业起底"
                    className="w-full rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm text-background-50 outline-none transition-colors focus:border-accent-400"
                  >
                    <option value="商业起底">商业起底</option>
                    <option value="行业内幕">行业内幕</option>
                    <option value="舆情深挖">舆情深挖</option>
                    <option value="全部栏目">全部栏目</option>
                  </select>
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="sub-message" className="text-sm font-medium text-background-200">
                    线索或建议（选填）
                  </label>
                  <textarea
                    id="sub-message"
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder="如果有带时间线的线索，欢迎简要描述（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-700 bg-background-950 px-4 py-3 text-sm leading-6 text-background-50 outline-none transition-colors placeholder:text-background-500 focus:border-accent-400"
                  />
                </div>

                <div className="form-guard-field" aria-hidden="true">
                  <label htmlFor="sub-website-alt">备用联系渠道</label>
                  <input
                    id="sub-website-alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>

                {status === "error" && formError && (
                  <p className="rounded-md border border-primary-500/40 bg-primary-500/10 px-4 py-3 text-sm text-primary-200">
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "submitting" ? "正在提交…" : "免费订阅深度周报"}
                </button>
                <p className="text-xs leading-6 text-background-400">
                  我们仅使用你的邮箱发送深度周报，不会用于其他用途，也不会分享给第三方。
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}